import React from 'react';
import { Controller, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { PersonalDataUserFormType } from 'entities/users/types';
import { Box, Button, FormControl, FormHelperText, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { CountrySelectId } from 'shared/consts';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

type PersonalDataCardPropsType = {
  form: UseFormReturn<PersonalDataUserFormType>;
  onSubmit: (data: PersonalDataUserFormType) => SubmitHandler<PersonalDataUserFormType>;
};
export const PersonalDataCard = ({ form, onSubmit }: PersonalDataCardPropsType) => {
  const { data: cities, isSuccess: isCitiesSuccess } = useGetCitiesQuery({ sort: 'country_id,name_ru' });

  const { control, watch, handleSubmit } = form;

  const cityIdWatch = watch('city_id');
  const countryIdWatch = watch('country_id');

  const selectCity = cities?.find((city) => city.id === cityIdWatch);
  const autocompleteCitiesOptions =
    countryIdWatch !== CountrySelectId.Empty
      ? cities?.filter((city) => city.country_id === countryIdWatch) || []
      : cities || [];

  const handleDateChange = (value: Dayjs | null, onChange: (...event: any[]) => void) => {
    onChange(value ? dayjs(value).format('YYYY-MM-DD') : null);
  };

  return (
    <>
      <Controller
        name="last_name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            type="text"
            label="Фамилия"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              marginTop: '30px',
              marginBottom: '24px',
            }}
            {...field}
          />
        )}
      />
      <Controller
        name="first_name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            type="text"
            label="Имя"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              marginBottom: '24px',
            }}
            {...field}
          />
        )}
      />
      <Controller
        name="middle_name"
        control={control}
        render={({ field }) => (
          <TextField
            type="text"
            label="Отчество"
            fullWidth
            size="small"
            sx={{
              marginBottom: '24px',
            }}
            {...field}
          />
        )}
      />
      <Controller
        name="iin"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            type="text"
            label={`${countryIdWatch === CountrySelectId.KG || countryIdWatch === CountrySelectId.UZ ? 'ИНН' : 'ИИН'}`}
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            sx={{
              marginBottom: '24px',
            }}
            {...field}
          />
        )}
      />
      <Controller
        name="city_id"
        control={control}
        render={({ field: { onChange }, fieldState }) => (
          <FormControl
            fullWidth
            error={!!fieldState.error}
            sx={{ marginBottom: '24px' }}
          >
            <Autocomplete
              disablePortal
              options={autocompleteCitiesOptions}
              value={selectCity || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(options) => options?.name_ru || options.name}
              loading={isCitiesSuccess}
              fullWidth
              loadingText="По вашему запросу ничего не найдено"
              onChange={(event, value) => onChange(value?.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Город"
                />
              )}
            />
            {fieldState.error?.message && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
          </FormControl>
        )}
      />
      {(countryIdWatch === CountrySelectId.KG || countryIdWatch === CountrySelectId.UZ) && (
        <Box sx={{ width: '100%' }}>
          <Controller
            control={control}
            name="document_number"
            render={({ field, fieldState }) => (
              <TextField
                label="Номер паспорта"
                size="small"
                fullWidth
                sx={{ marginBottom: '24px' }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" />
            <Controller
              control={control}
              name="issued_at"
              render={({ field: { onChange, value, ...restField }, fieldState }) => (
                <FormControl
                  error={!!fieldState.error}
                  sx={{ marginBottom: '24px', width: '60%' }}
                >
                  <DatePicker
                    label="Дата выдачи паспорта"
                    value={value ? dayjs(value) : null}
                    onChange={(value) => handleDateChange(value, onChange)}
                    format="DD/MM/YYYY"
                    {...restField}
                  />
                  {fieldState.error?.message && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="expiration_at"
              render={({ field: { onChange, value, ...restField }, fieldState }) => (
                <FormControl
                  error={!!fieldState.error}
                  sx={{ marginBottom: '24px', width: '60%' }}
                >
                  <DatePicker
                    label="Дата истечения срока паспорта"
                    value={value ? dayjs(value) : null}
                    onChange={(value) => handleDateChange(value, onChange)}
                    format="DD/MM/YYYY"
                    {...restField}
                  />
                  {fieldState.error?.message && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </LocalizationProvider>
          <Controller
            control={control}
            name="personal_number"
            render={({ field, fieldState }) => (
              <TextField
                label="Персональный номер"
                size="small"
                fullWidth
                sx={{ marginBottom: '24px' }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                {...field}
              />
            )}
          />
        </Box>
      )}
      <Button
        type="submit"
        variant="contained"
        onClick={handleSubmit(onSubmit)}
      >
        Сохранить
      </Button>
    </>
  );
};
